import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "../api/client";
import { useProjects } from "../context/ProjectContext";
import { toast } from "sonner";
import { BrandMark, BRAND } from "../components/Brand";
import { Button } from "../components/ui/button";
import {
  ArrowLeft, Buildings, FolderOpen, CheckCircle, Warning, Clock,
  ChartLineUp, ArrowSquareOut, Gear, ListChecks,
} from "@phosphor-icons/react";

const STATUS_BADGE = {
  Active: "bg-emerald-50 text-emerald-700 border-emerald-200",
  Inactive: "bg-neutral-100 text-neutral-600 border-neutral-200",
};

function BigCard({ label, value, suffix, accent, Icon }) {
  return (
    <div className="bg-white border border-[#E5E5E5] rounded-lg p-5" data-testid={`portfolio-total-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="flex items-start justify-between">
        <div>
          <p className="text-[10px] uppercase tracking-[0.25em] text-neutral-500 font-semibold" style={{ fontFamily: "IBM Plex Mono, monospace" }}>
            {label}
          </p>
          <div className="flex items-baseline gap-1 mt-3">
            <span className={`text-4xl font-black tracking-tight ${accent}`} style={{ fontFamily: "Chivo, sans-serif" }}>
              {value}
            </span>
            {suffix ? <span className="text-lg font-bold text-neutral-400">{suffix}</span> : null}
          </div>
        </div>
        <div className={`h-9 w-9 flex items-center justify-center border border-[#E5E5E5] rounded-md ${accent}`}>
          <Icon size={18} weight="bold" />
        </div>
      </div>
    </div>
  );
}

function MiniStat({ label, value, tone = "neutral", testId }) {
  const tones = {
    neutral: "text-neutral-900",
    emerald: "text-emerald-700",
    amber: "text-amber-700",
    red: "text-red-700",
    sky: "text-sky-700",
    indigo: "text-indigo-700",
  };
  return (
    <div className="flex flex-col" data-testid={testId}>
      <span className="text-[9px] uppercase tracking-[0.2em] text-neutral-500 font-semibold">{label}</span>
      <span className={`text-lg font-black tabular-nums ${tones[tone]}`} style={{ fontFamily: "Chivo, sans-serif" }}>{value}</span>
    </div>
  );
}

export default function Portfolio() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const { setProjectId } = useProjects();
  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/portfolio/summary");
        setData(data);
      } catch (e) {
        toast.error(e.response?.data?.detail || "Failed to load portfolio");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const openTracker = (projectId) => {
    setProjectId(projectId);
    navigate(`/?project_id=${projectId}`);
  };

  const totals = data?.totals || {};
  const customers = data?.customers || [];

  return (
    <div className="min-h-screen bg-[#FAFAFA] flex flex-col" data-testid="portfolio-page">
      <header className="h-16 border-b border-[#E5E5E5] bg-white flex items-center px-6 justify-between shrink-0">
        <div className="flex items-center gap-3">
          <BrandMark />
          <div className="flex flex-col leading-tight">
            <span className="text-sm font-black tracking-tight" style={{ fontFamily: "Chivo, sans-serif" }}>
              PROJECT TRACKER
            </span>
            <span className="text-[10px] uppercase tracking-[0.2em] text-neutral-500">Portfolio Overview</span>
          </div>
        </div>
        <Link to="/">
          <Button variant="outline" size="sm" data-testid="back-to-dashboard-btn" className="h-9">
            <ArrowLeft size={16} weight="bold" className="mr-2" />
            Back to Tracker
          </Button>
        </Link>
      </header>

      <div className="flex-1 p-6 max-w-6xl w-full mx-auto space-y-6">
        <div>
          <p className="text-[10px] uppercase tracking-[0.3em] text-neutral-500" style={{ fontFamily: "IBM Plex Mono, monospace" }}>
            Cross-project rollup
          </p>
          <h1 className="text-3xl font-black tracking-tight mt-1" style={{ fontFamily: "Chivo, sans-serif" }}>
            Portfolio
          </h1>
          <p className="text-sm text-neutral-500 mt-1">
            Every customer and project you have access to, with live task metrics rolled up in one place.
          </p>
        </div>

        {loading ? (
          <div className="text-sm text-neutral-500 py-14 text-center">Loading portfolio…</div>
        ) : (
          <>
            {/* Portfolio totals */}
            <div className="grid grid-cols-2 lg:grid-cols-5 gap-4" data-testid="portfolio-totals">
              <BigCard label="Customers"      value={totals.customers || 0} accent="text-[#387EE6]"  Icon={Buildings} />
              <BigCard label="Projects"       value={totals.projects || 0}  accent="text-sky-700"   Icon={FolderOpen} />
              <BigCard label="Total Tasks"    value={totals.tasks || 0}     accent="text-neutral-900" Icon={ListChecks} />
              <BigCard label="At Risk"        value={totals.at_risk || 0}   accent="text-amber-600" Icon={Warning} />
              <BigCard label="Avg Completion" value={totals.avg_completion || 0} suffix="%" accent="text-indigo-600" Icon={ChartLineUp} />
            </div>

            {/* Customer sections */}
            {customers.length === 0 ? (
              <div className="bg-white border border-[#E5E5E5] rounded-lg p-12 text-center">
                <Buildings size={40} weight="thin" className="text-neutral-400 mx-auto mb-3" />
                <p className="text-neutral-600 text-sm mb-4">No customers or projects yet.</p>
                <Link to="/setup">
                  <Button className="bg-[#387EE6] hover:bg-[#2563EB] text-white">
                    <Gear size={14} weight="bold" className="mr-2" /> Go to Setup
                  </Button>
                </Link>
              </div>
            ) : (
              <div className="space-y-4">
                {customers.map((c) => (
                  <div key={c.id} className="bg-white border border-[#E5E5E5] rounded-lg overflow-hidden" data-testid={`portfolio-customer-${c.id}`}>
                    <div className="flex items-center gap-3 px-5 py-4 border-b border-[#E5E5E5] bg-[#FAFAFA]">
                      <Buildings size={18} weight="bold" className="text-neutral-500" />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-black text-lg tracking-tight" style={{ fontFamily: "Chivo, sans-serif" }}>
                            {c.name}
                          </span>
                          <span className={`text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded border ${STATUS_BADGE[c.status]}`}>{c.status}</span>
                        </div>
                        {c.description ? <p className="text-xs text-neutral-500 mt-0.5 truncate">{c.description}</p> : null}
                      </div>
                      <div className="flex items-center gap-6 pl-4 border-l border-neutral-200">
                        <MiniStat label="Projects" value={c.stats.projects} testId={`cust-${c.id}-projects`} />
                        <MiniStat label="Tasks" value={c.stats.total} testId={`cust-${c.id}-total`} />
                        <MiniStat label="At Risk" value={c.stats.at_risk} tone={c.stats.at_risk > 0 ? "amber" : "neutral"} testId={`cust-${c.id}-at-risk`} />
                        <MiniStat label="Overdue" value={c.stats.overdue} tone={c.stats.overdue > 0 ? "red" : "neutral"} testId={`cust-${c.id}-overdue`} />
                        <MiniStat label="Avg %" value={`${c.stats.avg_completion}%`} tone="indigo" testId={`cust-${c.id}-avg`} />
                      </div>
                    </div>
                    <div className="p-4 space-y-2">
                      {c.projects.length === 0 ? (
                        <p className="text-xs text-neutral-500 italic px-2">No accessible projects for this customer.</p>
                      ) : c.projects.map((p) => {
                        const s = p.stats;
                        const pct = Math.max(0, Math.min(100, s.avg_completion || 0));
                        return (
                          <div key={p.id} className="border border-[#E5E5E5] rounded p-3 flex items-center gap-4 flex-wrap" data-testid={`portfolio-project-${p.id}`}>
                            <FolderOpen size={16} weight="bold" className="text-sky-700 shrink-0" />
                            <div className="min-w-0 flex-1">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="font-semibold text-sm text-neutral-900">{p.name}</span>
                                {p.code ? <span className="text-[10px] font-mono bg-neutral-100 px-1.5 py-0.5 rounded">{p.code}</span> : null}
                                <span className={`text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded border ${STATUS_BADGE[p.status]}`}>{p.status}</span>
                                {s.activity_today > 0 ? (
                                  <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded border text-sky-700 bg-sky-50 border-sky-200 flex items-center gap-1">
                                    <Clock size={10} weight="bold" /> {s.activity_today} today
                                  </span>
                                ) : null}
                              </div>
                            </div>

                            <div className="w-40 flex flex-col gap-1">
                              <div className="flex items-baseline justify-between">
                                <span className="text-[10px] uppercase tracking-wider text-neutral-500 font-semibold">Completion</span>
                                <span className="text-xs font-mono tabular-nums text-neutral-900 font-semibold">{s.avg_completion}%</span>
                              </div>
                              <div className="h-1.5 bg-neutral-200 rounded-full overflow-hidden">
                                <div className={`h-full ${pct >= 100 ? "bg-emerald-600" : pct >= 60 ? "bg-sky-600" : "bg-amber-500"}`} style={{ width: `${pct}%` }} />
                              </div>
                            </div>

                            <div className="flex items-center gap-5">
                              <MiniStat label="Tasks" value={s.total} testId={`proj-${p.id}-total`} />
                              <MiniStat label="On Track" value={s.on_track} tone="emerald" />
                              <MiniStat label="At Risk" value={s.at_risk} tone={s.at_risk > 0 ? "amber" : "neutral"} testId={`proj-${p.id}-at-risk`} />
                              <MiniStat label="Overdue" value={s.overdue} tone={s.overdue > 0 ? "red" : "neutral"} testId={`proj-${p.id}-overdue`} />
                              <MiniStat label="Done" value={s.completed} tone="indigo" />
                            </div>

                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => openTracker(p.id)}
                              data-testid={`portfolio-open-tracker-${p.id}`}
                              className="h-8"
                            >
                              <ArrowSquareOut size={12} weight="bold" className="mr-1" /> Open
                            </Button>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
